import React from 'react'
import CommonAttachmentModal from '../../../../components/commonAttachmentModal/commonAttachmentModal'
import Spinner from '../../../../components/common/spinner/spinner'

/**
 * TransactionAttachmentModal Component
 * 
 * A specialized attachment modal for transaction documents with user permissions
 * and date-based restrictions.
 * 
 * @param {Object} props
 * @param {boolean} props.visible - Controls modal visibility
 * @param {Function} props.onClose - Handler for closing the modal
 * @param {Object} props.transaction - Current transaction with attachments
 * @param {Array} props.fileList - List of files being uploaded
 * @param {Function} props.setFileList - Function to update file list
 * @param {Object} props.actionLoading - Loading states for various actions
 * @param {boolean} props.isCurrentDatePrevMonth - Whether viewing a previous month
 * @param {Object} props.currentUser - Current logged in user information
 * @param {Function} props.handleUploadFile - Handler for file upload
 * @param {Function} props.handlePreviewDocument - Handler for document preview
 * @param {Function} props.handleDownloadDocument - Handler for document download
 * @param {Function} props.handleDeleteDocument - Handler for document deletion
 */
const AttachmentModal = ({
  visible,
  onClose,
  transaction,
  fileList,
  setFileList,
  actionLoading,
  isCurrentDatePrevMonth,
  currentUser,
  handleUploadFile,
  handlePreviewDocument,
  handleDownloadDocument,
  handleDeleteDocument
}) => {
  const isCreatedByCurrentUser = React.useMemo(() => {
    if (!currentUser || !transaction) return false

    const emailMatch = transaction.createdByEmail?.toLowerCase() === currentUser.username?.toLowerCase()
    const nameMatch = transaction.createdBy?.toLowerCase() === currentUser.name?.toLowerCase()

    return emailMatch || nameMatch
  }, [currentUser, transaction])

  const disableModifications = isCurrentDatePrevMonth || !isCreatedByCurrentUser

  const disabledTooltipMessage = React.useMemo(() => {
    if (isCurrentDatePrevMonth) {
      return 'Cannot modify attachments from previous months'
    }
    if (!isCreatedByCurrentUser) {
      return 'You can only modify attachments for transactions you created'
    }
    return ''
  }, [isCurrentDatePrevMonth, isCreatedByCurrentUser])

  const config = {
    enableUpload: true,
    enableDownload: true,
    enablePreview: true,
    enableDelete: true,
    disableModifications,
    disabledTooltipMessage,
    showOriginalFilename: true,
    maxFileSize: 10,
    acceptedTypes: ['image/*', '.pdf', 'application/pdf'],
    allowedMimeTypes: [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'image/jpg',
      'image/gif',
      'image/bmp',
      'image/webp'
    ],
    uploadText: 'Click or drag files to this area to upload',
    uploadHint: 'Support for single or bulk upload. Image and PDF files are allowed. Files will be automatically saved.',
    multiple: true
  }

  const sanitizeFileName = React.useCallback((file) => {
    if (!file?.name) return file

    const newName = file.name.replace(/\s+/g, '_')
    if (newName === file.name) return file

    const sanitizedFile = new File([file], newName, { type: file.type })

    Object.keys(file).forEach(key => {
      if (key !== 'name') {
        try {
          sanitizedFile[key] = file[key]
        } catch {
          // Some properties might be read-only
        }
      }
    })

    return sanitizedFile
  }, [])

  const handlers = React.useMemo(() => ({
    handleUploadFile: (file) => {
      const sanitizedFile = sanitizeFileName(file)
      return handleUploadFile(sanitizedFile)
    },

    handlePreviewDocument: (doc) => {
      handlePreviewDocument(doc)
    },

    handleDownloadDocument: (doc) => {
      handleDownloadDocument(doc)
    },

    handleDeleteDocument: (docId, attachmentId) => {
      handleDeleteDocument(docId, attachmentId)
    },

    onAttachmentsUpdate: () => {
      // Handle any additional logic needed for transaction attachments
    }
  }), [sanitizeFileName, handleUploadFile, handlePreviewDocument, handleDownloadDocument, handleDeleteDocument])

  const loadingStates = React.useMemo(() => ({
    uploadAttachment: actionLoading.uploadAttachment || false,
    previewAttachment: actionLoading.previewAttachment || {},
    downloadAttachment: actionLoading.downloadAttachment || {},
    deleteAttachment: actionLoading.deleteAttachment || {}
  }), [actionLoading])

  const CustomSpinner = React.useCallback(() => (
    <div className="upload-loading">
      <Spinner size="small" />
      <p>Uploading...</p>
    </div>
  ), [])

  if (!transaction) {
    return null
  }

  return (
    <CommonAttachmentModal
      visible={visible}
      onClose={onClose}
      title={`Attachments for ${transaction?.transactionId || 'Transaction'}`}
      existingAttachments={transaction?.attachments || []}
      fileList={fileList}
      setFileList={setFileList}
      config={config}
      handlers={handlers}
      loadingStates={loadingStates}
      customSpinner={CustomSpinner}
    />
  )
}

export default AttachmentModal